/*
  Flavor switcher: swaps #MainContent to another blend without a page load.
  Blend pages are preloaded in the background; a click swaps the page body,
  hands Kaching Bundles the new product data, re-runs Loox, and updates the URL.
  If anything looks wrong afterwards (above all, Kaching not pointing at the
  blend on screen) it falls back to a normal page load.
*/
(() => {
  if (window.__flavorSwitcher) return;
  window.__flavorSwitcher = true;
  if (!window.fetch || !window.DOMParser || !history.pushState) return;

  const MAIN = '#MainContent';
  const LINK = '[data-flavor-switcher] a[data-flavor-handle]';
  const KACHING_TAGS = 'script[class^="kaching-bundles"]';
  const CHECK_TIMEOUT = 3000;
  const HOLD_MS = 1200;

  const pages = new Map(); // pathname -> Promise<Document>
  const ready = new Map(); // pathname -> Document, once loaded
  const loadedScripts = new Set([...document.scripts].map((s) => s.src).filter(Boolean));
  let swapToken = 0;

  function load(path) {
    if (!pages.has(path)) {
      const request = fetch(path, { credentials: 'same-origin' })
        .then((res) => {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          preloadHeroImages(doc);
          ready.set(path, doc);
          return doc;
        });
      request.catch(() => pages.delete(path));
      pages.set(path, request);
    }
    return pages.get(path);
  }

  function preloadHeroImages(doc) {
    doc.querySelectorAll(MAIN + ' media-gallery img, ' + LINK + ' img').forEach((src, i) => {
      if (i > 6) return;
      const img = new Image();
      if (src.getAttribute('sizes')) img.sizes = src.getAttribute('sizes');
      if (src.getAttribute('srcset')) img.srcset = src.getAttribute('srcset');
      img.src = src.getAttribute('src');
    });
  }

  // Includes the current blend, so switching back to it is instant too.
  function preloadBlends() {
    document.querySelectorAll(LINK).forEach((a) => load(a.pathname));
  }

  // Inline scripts inserted via DOM APIs never run; recreate them so they do.
  // External scripts already on the page are skipped to avoid double-loading.
  function reviveScripts(root) {
    root.querySelectorAll('script').forEach((old) => {
      const type = old.getAttribute('type');
      if (type && !/javascript|module/i.test(type)) return;
      if (old.src) {
        if (loadedScripts.has(old.src)) return;
        loadedScripts.add(old.src);
      }
      const script = document.createElement('script');
      for (const attr of old.attributes) script.setAttribute(attr.name, attr.value);
      script.textContent = old.textContent;
      old.replaceWith(script);
    });
  }

  function swapKachingData(doc) {
    const incoming = doc.querySelectorAll(KACHING_TAGS);
    if (!incoming.length) return false;
    document.querySelectorAll(KACHING_TAGS).forEach((tag) => tag.remove());
    incoming.forEach((tag) => document.head.appendChild(document.importNode(tag, true)));
    return true;
  }

  function rerunLoox() {
    const loader = document.querySelector('script[src*="loox.io/widget"]');
    if (!window.LOOX || !loader) return;
    window.LOOX.widgetRunOnce = false;
    const script = document.createElement('script');
    script.src = loader.src;
    script.async = true;
    document.head.appendChild(script);
  }

  // The theme's custom elements (product-info, animate-element, ...) slide in
  // when connected unless theme.config.motionReduced is set. On a swap the page
  // is already on screen, so replaying that would move the picker out from
  // under the shopper's thumb. Flip the flag only while the new content connects.
  function withoutEntranceAnimations(insert) {
    const config = window.theme && window.theme.config;
    if (!config) return insert();
    const previous = config.motionReduced;
    config.motionReduced = true;
    try {
      insert();
    } finally {
      config.motionReduced = previous;
    }
  }

  // SplitWords bails out of connectedCallback under motionReduced WITHOUT
  // removing .js-invisible — the reveal only happens at the end of its
  // animation path — so titles swapped in during withoutEntranceAnimations
  // stay visibility:hidden. Finish the theme's own end-state for them here.
  function revealSplitWords(root) {
    root.querySelectorAll('split-words.js-invisible').forEach((el) => el.classList.remove('js-invisible'));
  }

  // Widgets that render client-side (Loox stars) arrive empty in the swapped
  // HTML and fill in later, pushing everything below them down. Reserve the
  // space their block took on the outgoing page so nothing shifts when they
  // fill in. (Loox hides .loox-rating until drawn, so reserve on the block.)
  const LATE_WIDGETS = '.loox-rating';
  const widgetBlock = (el) => el.closest('.shopify-block') || el.parentElement;
  function reserveWidgetSpace(from, to) {
    const heights = [...from.querySelectorAll(LATE_WIDGETS)].map((el) => widgetBlock(el).getBoundingClientRect().height);
    to.querySelectorAll(LATE_WIDGETS).forEach((el, i) => {
      if (heights[i]) widgetBlock(el).style.minHeight = heights[i] + 'px';
    });
  }

  // Descriptions differ in length per blend, and the picker sits under the
  // description, so it would move on every switch. Measure every blend's
  // description at the current column width and give the description the
  // tallest height from the first switch on; then nothing above the picker
  // changes size between blends.
  const DESCRIPTION = '.product__text';
  let descriptionHeight = 0;
  let measuredWidth = 0;
  let measuredCount = 0;

  function descriptionOf(root) {
    const picker = root.querySelector('[data-flavor-switcher]');
    let el = picker && picker.previousElementSibling;
    while (el && !el.matches(DESCRIPTION)) el = el.previousElementSibling;
    return el;
  }

  // Only blends that have finished preloading are measured, so an early tap
  // never waits on a slow download; late arrivals are picked up next switch.
  function tallestDescription() {
    const current = descriptionOf(document);
    if (!current) return 0;
    const width = current.getBoundingClientRect().width;
    if (width !== measuredWidth) descriptionHeight = 0;
    const sources = [current, ...[...ready.values()].map(descriptionOf)].filter(Boolean);
    if (descriptionHeight && sources.length === measuredCount) return descriptionHeight;
    const probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = `position:absolute;left:-9999px;top:0;visibility:hidden;width:${width}px;`;
    current.parentElement.appendChild(probe);
    let tallest = 0;
    for (const source of sources) {
      const copy = document.importNode(source, true);
      copy.style.minHeight = '';
      probe.replaceChildren(copy);
      tallest = Math.max(tallest, copy.getBoundingClientRect().height);
    }
    probe.remove();
    descriptionHeight = Math.ceil(tallest);
    measuredWidth = width;
    measuredCount = sources.length;
    return descriptionHeight;
  }

  // Kaching draws its bundle box a few frames after the swap. Until then the
  // product column is ~400px shorter, and on desktop the sticky gallery next to
  // it runs out of room and rides up. Hold the column at its old height until
  // the new Kaching box is in.
  function reserveColumn(from, to) {
    const oldColumn = from.querySelector('product-info');
    const newColumn = to.querySelector('product-info');
    if (!oldColumn || !newColumn) return () => {};
    newColumn.style.minHeight = oldColumn.getBoundingClientRect().height + 'px';
    return () => { newColumn.style.minHeight = ''; };
  }

  function reserveDescription(root, height) {
    const description = descriptionOf(root);
    if (description && height) description.style.minHeight = height + 'px';
  }

  // Browser scroll anchoring adjusts the scroll when content above the viewport
  // is replaced, which jumps the page (e.g. the desktop gallery). Pause it from
  // just before the swap until holdPicker is done.
  const root = document.documentElement;
  const anchoringDefault = root.style.overflowAnchor;
  const pauseScrollAnchoring = () => { root.style.overflowAnchor = 'none'; };
  const restoreScrollAnchoring = () => { root.style.overflowAnchor = anchoringDefault; };

  // Keep the picker where the shopper's thumb is. Content above it (a longer
  // description, Loox stars filling in) settles over the first moments, so
  // re-anchor every frame for a short window, and stop as soon as the shopper
  // scrolls on their own.
  function holdPicker(top, token) {
    const until = performance.now() + HOLD_MS;
    let released = false;
    const release = () => { released = true; };
    const userInput = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    userInput.forEach((type) => window.addEventListener(type, release, { once: true, passive: true }));
    const picker = document.querySelector('[data-flavor-switcher]');
    if (!picker) return restoreScrollAnchoring();
    const align = () => {
      if (released || token !== swapToken) return;
      const drift = picker.getBoundingClientRect().top - top;
      if (Math.abs(drift) >= 1) window.scrollBy(0, drift);
    };
    // ResizeObserver runs after layout but before paint, so late content
    // (e.g. Loox stars) is corrected in the same frame instead of flickering.
    const column = picker.closest('product-info') || picker.parentElement;
    const observer = window.ResizeObserver ? new ResizeObserver(align) : null;
    if (observer) observer.observe(column);
    (function frame() {
      if (released || token !== swapToken || !picker.isConnected || performance.now() > until) {
        userInput.forEach((type) => window.removeEventListener(type, release));
        if (observer) observer.disconnect();
        if (token === swapToken) restoreScrollAnchoring();
        return;
      }
      align();
      requestAnimationFrame(frame);
    })();
  }

  function updateHead(doc) {
    document.title = doc.title;
    const canonical = document.querySelector('link[rel="canonical"]');
    const nextCanonical = doc.querySelector('link[rel="canonical"]');
    if (canonical && nextCanonical) canonical.href = nextCanonical.href;
  }

  function kachingMatches(handle) {
    const block = document.querySelector('kaching-bundles-block');
    if (!block) return false;
    try {
      return JSON.parse(block.getAttribute('product')).handle === handle;
    } catch (e) {
      return false;
    }
  }

  function verifyKaching(handle, token, url, onReady) {
    const started = Date.now();
    (function poll() {
      if (token !== swapToken) return;
      if (kachingMatches(handle) && document.querySelector('kaching-bundles-block').offsetHeight > 0) {
        onReady();
        return;
      }
      if (Date.now() - started > CHECK_TIMEOUT) {
        location.assign(url);
        return;
      }
      setTimeout(poll, 150);
    })();
  }

  async function swapTo(url, { push }) {
    const token = ++swapToken;
    const target = new URL(url, location.origin);
    const main = document.querySelector(MAIN);
    const picker = document.querySelector('[data-flavor-switcher]');
    const pickerTop = picker ? picker.getBoundingClientRect().top : null;
    // On phones the tins scroll sideways; the fresh row would start at the left edge.
    const rowScroll = picker ? picker.querySelector('.flavor-switcher__list').scrollLeft : 0;

    try {
      main.classList.add('is-flavor-swapping');
      const doc = await load(target.pathname);
      if (token !== swapToken) return;

      const nextMain = doc.querySelector(MAIN);
      const handle = target.pathname.split('/').filter(Boolean).pop();
      if (!nextMain) throw new Error('No #MainContent in ' + target.pathname);

      const reserved = tallestDescription();
      const hasKaching = swapKachingData(doc);
      const fresh = document.importNode(nextMain, true);
      reserveWidgetSpace(main, fresh);
      reserveDescription(fresh, reserved);
      const releaseColumn = reserveColumn(main, fresh);
      pauseScrollAnchoring();
      withoutEntranceAnimations(() => main.replaceWith(fresh));
      revealSplitWords(fresh);
      const row = fresh.querySelector('[data-flavor-switcher] .flavor-switcher__list');
      if (row) row.scrollLeft = rowScroll;
      reviveScripts(fresh);
      updateHead(doc);
      if (push) history.pushState({ flavorSwitcher: true }, '', target.href);

      if (pickerTop !== null) holdPicker(pickerTop, token);
      else restoreScrollAnchoring();

      if (hasKaching && typeof window.kachingBundlesInitialize === 'function') {
        window.kachingBundlesInitialize();
        verifyKaching(handle, token, target.href, releaseColumn);
      } else {
        releaseColumn();
      }
      rerunLoox();
      document.dispatchEvent(new CustomEvent('flavor-switcher:swapped', { detail: { handle } }));
    } catch (err) {
      console.error('[flavor-switcher]', err);
      location.assign(target.href);
    }
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest(LINK);
    if (!link) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (link.getAttribute('aria-current') === 'true') return;
    swapTo(link.href, { push: true });
  });

  const warm = (event) => {
    const link = event.target.closest && event.target.closest(LINK);
    if (link) load(link.pathname);
  };
  document.addEventListener('pointerover', warm, { passive: true });
  document.addEventListener('touchstart', warm, { passive: true });

  // Phones: if the current blend's tin starts off-screen in the sideways row, bring it in.
  const currentTile = document.querySelector(LINK + '[aria-current="true"]');
  if (currentTile) {
    const row = currentTile.closest('.flavor-switcher__list');
    const tile = currentTile.getBoundingClientRect();
    const bounds = row.getBoundingClientRect();
    if (tile.right > bounds.right) row.scrollLeft += tile.right - bounds.right + 20;
  }

  history.replaceState(Object.assign({}, history.state, { flavorSwitcher: true }), '');
  window.addEventListener('popstate', (event) => {
    if (event.state && event.state.flavorSwitcher) swapTo(location.href, { push: false });
  });

  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 2000));
  idle(preloadBlends, { timeout: 2000 });
})();
